import { saveTranscript } from '@/infrastructure/storage'
import type { MediaTranscript } from '@/types/storage'
import { mediaTranscriptionService } from '@/features/media-library/services/media-transcription-service'

function splitAssistantTranscriptText(text: string): string[] {
  const normalized = text
    .replace(/\s+/g, ' ')
    .replace(/[\r\n]+/g, ' ')
    .trim()

  if (!normalized) {
    return []
  }

  const sentenceLikeParts = normalized
    .split(/(?<=[。！？!?；;])/u)
    .map((part) => part.trim())
    .filter(Boolean)

  const coarseParts = sentenceLikeParts.length > 0 ? sentenceLikeParts : [normalized]
  const fineParts = coarseParts.flatMap((part) => {
    if (part.length <= 28) {
      return [part]
    }

    return part
      .split(/(?<=[，,、：:])/u)
      .map((piece) => piece.trim())
      .filter(Boolean)
  })

  const segments: string[] = []
  for (const part of fineParts) {
    if (!part) {
      continue
    }

    const current = segments.at(-1)
    if (current && current.length < 18 && current.length + part.length <= 30) {
      segments[segments.length - 1] = `${current}${part}`
      continue
    }

    segments.push(part)
  }

  return segments.length > 0 ? segments : [normalized]
}

export async function createTranscriptForGeneratedAudio(
  mediaId: string,
  options: {
    text: string
    durationSeconds: number
    language?: string
  },
): Promise<MediaTranscript> {
  const normalizedText = options.text.trim()
  if (!normalizedText) {
    throw new Error('Missing generated voiceover text for transcript creation.')
  }

  const rawSegments = splitAssistantTranscriptText(normalizedText)
  const totalDuration = Math.max(options.durationSeconds, rawSegments.length * 0.8, 1)
  const weights = rawSegments.map((segment) => Math.max(segment.replace(/\s+/g, '').length, 1))
  const totalWeight = weights.reduce((sum, weight) => sum + weight, 0)
  let cursor = 0

  const segments = rawSegments.map((segment, index) => {
    const isLast = index === rawSegments.length - 1
    const segmentDuration = isLast
      ? Math.max(totalDuration - cursor, 0.2)
      : Math.max((totalDuration * weights[index]!) / totalWeight, 0.2)

    const start = Number(cursor.toFixed(3))
    const end = Number(Math.min(totalDuration, cursor + segmentDuration).toFixed(3))
    cursor = end

    return {
      text: segment,
      start,
      end: Math.max(end, start + 0.2),
    }
  })

  const now = Date.now()
  const transcript: MediaTranscript = {
    id: mediaId,
    mediaId,
    model: 'whisper-base',
    language: options.language,
    quantization: 'hybrid',
    text: normalizedText,
    segments,
    createdAt: now,
    updatedAt: now,
  }

  await saveTranscript(transcript)
  return transcript
}

export async function transcribeMedia(
  mediaId: string,
  options?: {
    language?: string
    signal?: AbortSignal
    onProgress?: (progress: unknown) => void
  },
): Promise<MediaTranscript> {
  const abortListener = () => {
    mediaTranscriptionService.cancelTranscription(mediaId)
  }

  if (options?.signal?.aborted) {
    abortListener()
  } else {
    options?.signal?.addEventListener('abort', abortListener, { once: true })
  }

  return mediaTranscriptionService
    .transcribeMedia(mediaId, {
      language: options?.language,
      onProgress: options?.onProgress as
        | ((
            progress: import('@/features/media-library/transcription/types').TranscribeProgress,
          ) => void)
        | undefined,
    })
    .finally(() => {
      options?.signal?.removeEventListener('abort', abortListener)
    })
}

export async function insertTranscriptAsCaptions(
  mediaId: string,
  options?: {
    clipIds?: readonly string[]
    replaceExisting?: boolean
    preferredTrackAboveTrackId?: string
  },
): Promise<{ insertedItemCount: number; removedItemCount: number }> {
  return mediaTranscriptionService.insertTranscriptAsCaptions(mediaId, {
    clipIds: options?.clipIds,
    replaceExisting: options?.replaceExisting,
    preferredTrackAboveTrackId: options?.preferredTrackAboveTrackId,
  })
}
